/**
 * @file
 * window.BerUi: the framework-agnostic Component and Container classes.
 * No Drupal here, ever.
 */

/* eslint-disable max-classes-per-file -- Component and Container are one
   foundation: Container extends Component and neither is useful alone. */
((global) => {
  const LOADING = 'ber-ui:loading';
  const LOADED = 'ber-ui:loaded';

  // Returned by Container.addOnce() once a key has been used: a chainable
  // no-op, so the add()/addComponent() that follows the guard does nothing.
  const NOOP = new Proxy({}, { get: () => () => NOOP });

  class Component {
    // An existing element adopts; a tag name or a selector creates:
    // 'button.tag__x.is-small[type=button]#id' sets classes, attributes
    // and id along the way.
    constructor(el = 'div', classes = '') {
      if (el instanceof Element) {
        this.el = el;
      } else {
        const [, tag = '', rest = ''] =
          String(el || 'div').match(/^([a-z0-9-]*)([\s\S]*)$/i) || [];
        this.el = document.createElement(tag || 'div');
        (rest.match(/[.#][^.#[\]]+|\[[^\]]+\]/g) || []).forEach((part) => {
          if (part[0] === '.') {
            this.el.classList.add(part.slice(1));
          } else if (part[0] === '#') {
            this.el.id = part.slice(1);
          } else {
            const [name, value = ''] = part.slice(1, -1).split('=');
            this.el.setAttribute(name, value);
          }
        });
      }
      if (classes) {
        this.el.classList.add(...classes.split(/\s+/).filter(Boolean));
      }
    }

    static create(...args) {
      return new this(...args);
    }

    getElement() {
      return this.el;
    }

    withClasses(...names) {
      this.el.classList.add(...names.filter(Boolean));
      return this;
    }

    withAttribute(name, value) {
      this.el.setAttribute(name, value);
      return this;
    }

    withAria(name, value) {
      return this.withAttribute(`aria-${name}`, value);
    }

    withRole(role) {
      return this.withAttribute('role', role);
    }

    withText(text) {
      this.el.textContent = text;
      return this;
    }

    on(type, handler, options) {
      this.el.addEventListener(type, handler, options);
      return this;
    }

    appendTo(target) {
      (target.el || target).appendChild(this.el);
      return this;
    }

    show() {
      this.el.hidden = false;
      return this;
    }

    hide() {
      this.el.hidden = true;
      return this;
    }

    enable() {
      this.el.disabled = false;
      return this;
    }

    disable() {
      this.el.disabled = true;
      return this;
    }

    // Reports async work upward; enclosing views show their loader while
    // any descendant is pending.
    setLoading(on) {
      this.el.dispatchEvent(
        new CustomEvent(on ? LOADING : LOADED, { bubbles: true }),
      );
      return this;
    }

    loading(on) {
      return this.setLoading(on);
    }
  }

  class Container extends Component {
    constructor(el, classes) {
      super(el, classes);
      this.children = [];
      // Subclasses with chrome (header/footer) point this at their body.
      this.slot = this.el;
    }

    add(child) {
      this.children.push(child);
      this.slot.appendChild(child.el || child);
      return this;
    }

    addComponent(child) {
      return this.add(child);
    }

    // Guard the next add in the chain, keyed: the first time a key is seen
    // this returns the container so the following addComponent()/add() runs;
    // afterwards it returns a chainable no-op. So
    // `container.addOnce(key).addComponent(child)` stays idempotent with no
    // DOM lookup. The key set lives on this container instance.
    addOnce(key) {
      this.keyed = this.keyed || new Set();
      if (this.keyed.has(key)) {
        return NOOP;
      }
      this.keyed.add(key);
      return this;
    }

    addAll(...children) {
      children.forEach((child) => this.add(child));
      return this;
    }

    addComponents(...children) {
      return this.addAll(...children);
    }

    clear() {
      this.slot.textContent = '';
      this.children = [];
      return this;
    }
  }

  global.BerUi = { Component, Container, events: { LOADING, LOADED } };
})(window);
